export const STAGES = [
  {
    key: "control", n: 1, title: "Development Control", short: "Development Control",
    goal: "Secure contractual development rights with the City.",
    risk: { dev: 5, construction: 1, capital: 1, time: 1 },
    sold: "A controlled development position — protected City rights, concept, and underwriting package.",
    retained: "Optional sponsor role / fee or carried interest if negotiated.",
    nextInvestor: "Entitlements, design, engineering, leasing, GC pricing, financing and full construction.",
    monetization: ["Sell / assign development position if permitted", "Introduce a larger developer", "Bring in Joint Venture capital", "Continue to entitlement"],
    explain: "Arqitek is monetizing a controlled development opportunity, not selling City-owned land.",
  },
  {
    key: "pad", n: 2, title: "Pad-Ready / Entitled", short: "Pad-Ready",
    goal: "Complete the entitlement and infrastructure path.",
    risk: { dev: 4, construction: 1, capital: 2, time: 2 },
    sold: "A substantially de-risked development position with the entitlement and infrastructure path established.",
    retained: "Optional sponsor / development-management participation.",
    nextInvestor: "Construction documents, permits, GC pricing, leasing execution, financing and construction.",
    monetization: ["Sell de-risked development rights", "Recapitalize with a JV partner"],
  },
  {
    key: "buildReady", n: 3, title: "Build-Ready", short: "Build-Ready",
    goal: "Plans, permits, pricing and leasing package in place.",
    risk: { dev: 3, construction: 2, capital: 3, time: 3 },
    sold: "A substantially shovel-ready commercial development opportunity.",
    retained: "Optional development fee / promoted interest if negotiated.",
    nextInvestor: "Close financing, build, lease-up and stabilize.",
    monetization: ["Strong pre-construction exit", "Buyer can move toward construction"],
  },
  {
    key: "sell", n: 4, title: "Build + Sell", short: "Build + Sell", tag: "Potential Cash Exit",
    goal: "Build, lease and sell the stabilized asset at the partner base value.",
    risk: { dev: 1, construction: 5, capital: 5, time: 4 },
    sold: "The stabilized, income-producing leasehold asset.",
    retained: "Cash profit; no ongoing ownership.",
    nextInvestor: "Operate the asset — buyer assumes asset-management and lease-renewal risk.",
  },
  {
    key: "hold", n: 5, title: "Build + Hold / Refinance", short: "Build + Hold", tag: "Potential Long-Term Wealth Strategy",
    goal: "Refinance at stabilization, return capital and retain ownership.",
    risk: { dev: 1, construction: 5, capital: 5, time: 5 },
    sold: "Nothing — refinance proceeds replace construction debt.",
    retained: "Continued ownership, recurring income, rent growth, appreciation, future refinance and sale optionality.",
    nextInvestor: "Not applicable — the JV continues to own and operate.",
  },
];

const MONTH_KEY = { control: "mControl", pad: "mPad", buildReady: "mBuildReady", sell: "mSell", hold: "mHold" };

const baseNumbers = (s, m, a) => {
  if (s.key === "sell") return { cum: a.tdc, inc: a.tdc - a.buildReadyCost, remaining: 0, value: m.netSale, valueLabel: "Net sale proceeds", created: m.profit, createdLabel: "Gross project profit" };
  if (s.key === "hold") return { cum: a.tdc, inc: a.tdc - a.buildReadyCost, remaining: 0, value: m.cashOut, valueLabel: "Net refinance cash-out", created: m.holdCreated, createdLabel: "Value created (incl. retained equity)" };
  const st = m.stages[s.key];
  const prevCum = s.key === "control" ? 0 : s.key === "pad" ? a.controlCost : a.padCost;
  return { cum: st.cum, inc: st.cum - prevCum, remaining: st.remaining, value: st.assignment, valueLabel: st.overridden ? "Your assignment price" : "Modeled gross assignment value", created: st.created, createdLabel: "Value created after spend + friction" };
};

export const stageNumbers = (s, m, a) => {
  const n = baseNumbers(s, m, a);
  const late = s.key === "sell" || s.key === "hold";
  const cash = late ? m.equity : n.cum;
  const months = a[MONTH_KEY[s.key]];
  const roc = cash ? n.created / cash : 0;
  return { ...n, cash, months, roc, annual: months ? roc / (months / 12) : 0, ...distribute(cash, n.created, months, a) };
};

export function distribute(cash, profit, months, a) {
  const pc = a.partnerCapPct / 100;
  const partnerCap = cash * pc, arqCap = cash - partnerCap;
  if (profit <= 0) return { partnerCap, arqCap, prefOwed: 0, prefPaid: 0, partnerPref: 0, arqPref: 0, residual: 0, arqSplit: 0, partnerSplit: 0, arq: profit * (1 - pc), partner: profit * pc };
  const prefOwed = cash * (a.prefRate / 100) * (months / 12);
  const prefPaid = Math.min(profit, prefOwed);
  const partnerPref = prefPaid * pc, arqPref = prefPaid - partnerPref;
  const residual = profit - prefPaid;
  const arqSplit = residual * (a.jvSplit / 100), partnerSplit = residual - arqSplit;
  return { partnerCap, arqCap, prefOwed, prefPaid, partnerPref, arqPref, residual, arqSplit, partnerSplit, arq: arqPref + arqSplit, partner: partnerPref + partnerSplit };
}
